import React from 'react'
import { Link } from 'react-router-dom'

export const GRNDaDBenefitsOfJoining: React.FC = () => {
  const benefits = [
    {
      title: 'Access to Site Data',
      description:
        'Participating sites gain access to their own comprehensive data analytics and reporting tools.',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      )
    },
    {
      title: 'Health Maintenance Sheets',
      description:
        'Receive standardized health maintenance tracking sheets for optimal patient care management.',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      )
    },
    {
      title: 'De-identified Data Access',
      description:
        'Access to de-identified aggregate data from all participating sites for research purposes.',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
          />
        </svg>
      )
    }
  ]

  const additionalBenefits = [
    'Collaborative research opportunities',
    'Access to expert consultation',
    'Participation in multi-site studies',
    'Professional development opportunities',
    'Networking with leading SCD researchers'
  ]

  return (
    <main
      className="min-h-screen bg-[#EDE6DE] px-4 py-12 md:px-8 lg:px-16"
      data-testid="grndad-benefits-container"
    >
      <div className="max-w-7xl mx-auto">
        {/* Sub-navigation */}
        <nav className="mb-8 border-b border-[#3C2416]/20 pb-4">
          <div className="flex flex-wrap gap-6">
            <Link
              to="/grndad/about"
              className="text-[#3C2416] hover:text-[#A6410A] transition-colors"
            >
              About
            </Link>
            <Link
              to="/grndad/locations"
              className="text-[#3C2416] hover:text-[#A6410A] transition-colors"
            >
              Locations
            </Link>
            <Link
              to="/grndad/executive-committee"
              className="text-[#3C2416] hover:text-[#A6410A] transition-colors"
            >
              Executive Committee
            </Link>
            <Link
              to="/grndad/benefits-of-joining"
              className="text-[#A6410A] font-medium border-b-2 border-[#A6410A] pb-1"
            >
              Benefits of Joining
            </Link>
            <Link
              to="/grndad/speaker-series"
              className="text-[#3C2416] hover:text-[#A6410A] transition-colors"
            >
              Speaker Series
            </Link>
          </div>
        </nav>

        <h1 className="text-4xl font-bold text-[#3C2416] mb-4">
          Benefits of Joining GRNDaD
        </h1>

        <div className="space-y-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#A6410A]"
              data-testid={`benefit-card-${index}`}
            >
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#A6410A]/10 rounded-full flex items-center justify-center text-[#A6410A]">
                    {benefit.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <h3
                    className="text-xl font-semibold text-[#3C2416] mb-3"
                    data-testid={`benefit-title-${index}`}
                  >
                    {benefit.title}
                  </h3>
                  <p className="text-[#3C2416] leading-relaxed text-lg">
                    {benefit.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Benefits Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold text-[#3C2416] mb-4">
            Additional Benefits
          </h2>
          <ul className="space-y-3">
            {additionalBenefits.map((benefit, index) => (
              <li key={index} className="flex items-center text-[#3C2416]">
                <svg
                  className="w-5 h-5 text-[#A6410A] mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* How to Join Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold text-[#3C2416] mb-4">
            How to Join
          </h2>
          <p className="text-[#3C2416] leading-relaxed mb-6">
            To learn more about joining GRNDaD as a participating site, please
            contact our team. We&apos;ll provide you with detailed information
            about the enrollment process, data requirements, and support
            available to new sites.
          </p>
        </div>

        {/* Contact Information Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold text-[#3C2416] mb-4">
            Contact Information
          </h2>
          <p className="text-[#3C2416] mb-4">
            For questions about joining GRNDaD or accessing data, please reach
            out to us:
          </p>
          <a
            href="mailto:GRNDaDSCD@gmail.com"
            className="text-[#A6410A] font-medium hover:underline text-lg"
          >
            GRNDaDSCD@gmail.com
          </a>
        </div>
      </div>
    </main>
  )
}
